/** ⌘K command palette: jump to a screen, or search customers and jobs (server search; debounced). */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useData } from '@app/core/provider';
import { useSession } from '@app/core/auth';
import { phone } from '@app/core/format';
import { statusLabel } from '@app/core/job_status';
import { Dialog } from './dialog';
import { visibleNav } from './nav';

export function Palette({ open, onClose, onGo }: { open: boolean; onClose: () => void; onGo: (to: string) => void }) {
  const { data } = useData();
  const { can, flag } = useSession();
  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [idx, setIdx] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 180); return () => clearTimeout(t); }, [q]);
  useEffect(() => { if (open) { setQ(''); setIdx(0); setTimeout(() => input.current?.focus(), 0); } }, [open]);
  const jobs = useQuery({ queryKey: ['palette', 'jobs', dq], queryFn: () => data.entities.jobs({ q: dq, limit: 6 }), enabled: open && dq.length >= 2 && can('jobs.view') });
  const custs = useQuery({ queryKey: ['palette', 'customers', dq], queryFn: () => data.entities.customers({ q: dq, limit: 6 }), enabled: open && dq.length >= 2 && can('customers.view') });
  const rows = useMemo(() => {
    const n = q.toLowerCase();
    const screens = visibleNav(can, flag).filter((x) => !n || x.label.toLowerCase().includes(n)).map((x) => ({ key: x.path, label: x.label, hint: 'Go to', to: x.path }));
    const j = (jobs.data?.items ?? []).map((x) => ({ key: `j${x.id}`, label: `${x.job_no} · ${x.customer_name}`, hint: statusLabel(x.order_status), to: `/jobs/${x.id}` }));
    const c = (custs.data?.items ?? []).map((x) => ({ key: `c${x.id}`, label: x.display_name, hint: phone(x.phone_e164), to: `/customers/${x.id}` }));
    return [...j, ...c, ...screens].slice(0, 14);
  }, [q, jobs.data, custs.data, can, flag]);
  return (
    <Dialog open={open} onClose={onClose} title="Search">
      <input ref={input} className="app-input app-palette__input" placeholder="Customer, phone, job number or screen" value={q}
        onChange={(e) => { setQ(e.target.value); setIdx(0); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(rows.length - 1, i + 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
          if (e.key === 'Enter' && rows[idx]) onGo(rows[idx]!.to);
        }} aria-label="Search" />
      <ul className="app-palette" role="listbox">
        {rows.map((r, i) => (
          <li key={r.key} role="option" aria-selected={i === idx}>
            <button type="button" className={i === idx ? 'is-on' : ''} onMouseEnter={() => setIdx(i)} onClick={() => onGo(r.to)}><span>{r.label}</span><span className="stg-muted">{r.hint}</span></button>
          </li>
        ))}
        {!rows.length && <li className="stg-muted app-palette__empty">No matches</li>}
      </ul>
    </Dialog>
  );
}
